import React, { useEffect } from 'react';

export const LocalBusinessSchema = ({ areaName = null }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "AutomotiveBusiness",
    "name": areaName ? `Propitstop - Car Service in ${areaName}` : "Propitstop",
    "image": "https://propitstop.in/propitstop-logo.jpg",
    "description": "Professional car service and repair in Bangalore. Quality service, transparent pricing, free pickup and drop, 6-month warranty on parts.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bangalore",
      "addressLocality": areaName || "Bangalore",
      "addressRegion": "Karnataka",
      "postalCode": "560077",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "12.9716",
      "longitude": "77.5946"
    },
    "url": areaName 
      ? `https://propitstop.in/car-service-${areaName.toLowerCase().replace(/\s+/g, '-')}`
      : "https://propitstop.in",
    "telephone": "+919110416663",
    "email": "support@propitstop.in",
    "priceRange": "₹₹",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "19:00"
      }
    ],
    "areaServed": {
      "@type": "City",
      "name": "Bangalore",
      "containedIn": {
        "@type": "State",
        "name": "Karnataka"
      }
    },
    "serviceType": [
      "Car Service",
      "Car Repair",
      "Periodic Services",
      "AC Service & Repair",
      "Denting & Painting",
      "Vehicle Inspection"
    ],
    "paymentAccepted": ["Cash", "UPI", "Credit Card", "Debit Card"],
    "currenciesAccepted": "INR"
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = `schema-local-business-${areaName || 'main'}`;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById(`schema-local-business-${areaName || 'main'}`);
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [areaName]);

  return null;
};

export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Propitstop",
    "alternateName": "Propitstop Car Services",
    "url": "https://propitstop.in",
    "logo": {
      "@type": "ImageObject",
      "url": "https://propitstop.in/propitstop-logo.jpg",
      "width": 600,
      "height": 60
    },
    "image": "https://propitstop.in/propitstop-logo.jpg",
    "description": "Your trusted partner for all car service needs in Bangalore. Quality service, transparent pricing, and customer satisfaction guaranteed.",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+919110416663",
      "contactType": "customer service",
      "areaServed": "IN",
      "availableLanguage": ["English", "Hindi", "Kannada"]
    },
    "sameAs": [
      "https://www.facebook.com/propitstop",
      "https://www.youtube.com/@Sathishd-k5e"
    ]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'schema-organization';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('schema-organization');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return null;
};

export const ServiceSchema = ({ service }) => {
  if (!service) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.name,
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "AutomotiveBusiness",
      "name": "Propitstop",
      "telephone": "+919110416663",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bangalore",
        "addressRegion": "Karnataka",
        "postalCode": "560077",
        "addressCountry": "IN"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "Bangalore"
    },
    "offers": {
      "@type": "Offer",
      "price": service.base_price,
      "priceCurrency": "INR"
    }
  };

  useEffect(() => {
    if (!service) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = `schema-service-${service.id || service.name}`;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById(`schema-service-${service.id || service.name}`);
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [service]);

  return null;
};

export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'schema-breadcrumb';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('schema-breadcrumb');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [items]);

  return null;
};

export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'schema-faq';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('schema-faq');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [faqs]);

  return null;
};

export const AggregateRatingSchema = ({ rating, reviewCount }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Propitstop",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": rating,
      "reviewCount": reviewCount,
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'schema-rating';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('schema-rating');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [rating, reviewCount]);

  return null;
};
